import React, { useState, useRef } from "react";
import { Input, Button, Space, Upload, message as antMessage, Form, Modal, Typography } from "antd";
import { SendOutlined, PaperClipOutlined, EditOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";

const { TextArea } = Input;
const { Text } = Typography;

interface MessageComposerProps {
  onSend: (messageData: {
    subject: string;
    body: string;
    attachments?: any[];
  }) => void;
}

export const MessageComposer: React.FC<MessageComposerProps> = ({ onSend }) => {
  const [form] = Form.useForm();
  const [sending, setSending] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [showFullComposer, setShowFullComposer] = useState(false);
  const quickMessageRef = useRef<HTMLTextAreaElement>(null);

  const handleQuickSend = async () => {
    const quickMessage = form.getFieldValue("quickMessage");
    if (!quickMessage?.trim()) {
      antMessage.warning("Bitte geben Sie eine Nachricht ein");
      return;
    }

    setSending(true);
    try {
      await onSend({
        subject: "Nachricht",
        body: quickMessage,
        attachments: [],
      });
      form.resetFields(["quickMessage"]);
    } catch (error) {
      antMessage.error("Fehler beim Senden der Nachricht");
    } finally {
      setSending(false);
    }
  };

  const handleFullSend = async () => {
    try {
      const values = await form.validateFields(["subject", "body"]);
      
      if (!values.body?.trim()) {
        antMessage.warning("Bitte geben Sie eine Nachricht ein");
        return;
      }

      setSending(true);
      await onSend({
        subject: values.subject || "Nachricht",
        body: values.body,
        attachments: fileList.map(file => ({
          name: file.name,
          size: file.size,
          type: file.type,
          url: file.response?.url || file.url,
        })),
      });

      form.resetFields();
      setFileList([]);
      setShowFullComposer(false);
    } catch (error) {
      antMessage.error("Fehler beim Senden der Nachricht");
    } finally {
      setSending(false);
    }
  };

  const uploadProps = {
    name: "file",
    multiple: true,
    fileList,
    onChange(info: any) {
      setFileList(info.fileList);
      
      const { status } = info.file;
      if (status === "done") {
        antMessage.success(`${info.file.name} erfolgreich hochgeladen.`);
      } else if (status === "error") {
        antMessage.error(`${info.file.name} Upload fehlgeschlagen.`);
      }
    },
    beforeUpload: (file: File) => {
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        antMessage.error("Datei muss kleiner als 10MB sein!");
      }
      return isLt10M;
    },
  };

  return (
    <>
      {/* Quick Message Input */}
      <Form form={form} className="message-composer">
        <Space.Compact style={{ width: "100%" }}>
          <Form.Item name="quickMessage" style={{ flex: 1, margin: 0 }}>
            <TextArea
              ref={quickMessageRef}
              placeholder="Nachricht eingeben..."
              autoSize={{ minRows: 1, maxRows: 4 }}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleQuickSend();
                }
              }}
            />
          </Form.Item>
          <Space size={4}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => setShowFullComposer(true)}
              title="Erweiterten Editor öffnen"
              style={{ marginLeft: 8 }}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleQuickSend}
              loading={sending}
              disabled={!form.getFieldValue("quickMessage")?.trim()}
              title="Nachricht senden"
            />
          </Space>
        </Space.Compact>
      </Form>

      {/* Full Composer Modal */}
      <Modal
        title="Neue Nachricht verfassen"
        open={showFullComposer}
        onOk={handleFullSend}
        onCancel={() => setShowFullComposer(false)}
        okText="Senden"
        cancelText="Abbrechen"
        confirmLoading={sending}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="subject"
            label="Betreff"
            rules={[{ required: true, message: "Bitte geben Sie einen Betreff ein" }]}
          >
            <Input placeholder="Betreff eingeben..." />
          </Form.Item>

          <Form.Item
            name="body"
            label="Nachricht"
            rules={[{ required: true, message: "Bitte geben Sie eine Nachricht ein" }]}
          >
            <TextArea
              rows={8}
              placeholder="Ihre Nachricht..."
              showCount
              maxLength={5000}
            />
          </Form.Item>

          <Form.Item label="Anhänge">
            <Upload {...uploadProps}>
              <Button icon={<PaperClipOutlined />}>Datei anhängen</Button>
            </Upload>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Max. 10MB pro Datei
            </Text>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}; 